import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const ContentAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('news'); // news or events
  const [view, setView] = useState('list'); // list or create or edit
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [selectedEventRsvps, setSelectedEventRsvps] = useState([]);
  const [rsvpCounts, setRsvpCounts] = useState({});
  
  // Data states
  const [newsItems, setNewsItems] = useState([]);
  const [eventItems, setEventItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    category: '',
    location: '', // for events only
    event_date: '', // for events only
    event_time: '', // for events only
    rsvp_deadline: '', // for events only
    target_tiers: [], // for events only - membership tiers
    is_published: false
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (adminInfo) {
      fetchData();
    }
  }, [activeTab, adminInfo]);

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate('/content-admin-login');
      return;
    }

    // Check if user is content admin
    const { data: adminData, error: adminError } = await supabase
      .from('content_admins')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    if (adminError || !adminData) {
      setError('Access denied. Content admin privileges required.');
      setTimeout(() => navigate('/content-admin-login'), 2000);
      return;
    }

    setAdminInfo(adminData);
    setLoading(false);
  };

  const fetchData = async () => {
    try {
      if (activeTab === 'news') {
        const { data, error } = await supabase
          .from('news')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setNewsItems(data || []);
      } else {
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .order('event_date', { ascending: false });
        
        if (error) throw error;
        setEventItems(data || []);
        
        // Fetch RSVP counts for each event
        await fetchRsvpCounts(data || []);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch data: ' + err.message);
    }
  };

  const fetchRsvpCounts = async (events) => {
    try {
      const eventIds = events.map(e => e.id);
      if (eventIds.length === 0) return;

      const { data, error } = await supabase
        .from('event_rsvps')
        .select('event_id')
        .in('event_id', eventIds)
        .eq('rsvp_status', 'confirmed');
      
      if (error) throw error;

      const counts = {};
      (data || []).forEach(rsvp => {
        counts[rsvp.event_id] = (counts[rsvp.event_id] || 0) + 1;
      });
      setRsvpCounts(counts);
    } catch (err) {
      console.error('Error fetching RSVP counts:', err);
    }
  };

  const fetchEventRsvps = async (eventId) => {
    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('*')
        .eq('event_id', eventId)
        .eq('rsvp_status', 'confirmed')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setSelectedEventRsvps(data || []);
      setShowRsvpModal(true);
    } catch (err) {
      console.error('Error fetching RSVPs:', err);
      alert('Failed to fetch RSVPs: ' + err.message);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Auto-generate slug from title
    if (name === 'title' && !editingItem) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  const handleTierChange = (tier) => {
    setFormData(prev => {
      const currentTiers = prev.target_tiers || [];
      
      // Toggle tier selection
      if (currentTiers.includes(tier)) {
        return {
          ...prev,
          target_tiers: currentTiers.filter(t => t !== tier)
        };
      } else {
        return {
          ...prev,
          target_tiers: [...currentTiers, tier]
        };
      }
    });
  };

  const handleSelectAllTiers = () => {
    const allTiers = ['Silver', 'Gold', 'Platinum', 'LGC'];
    setFormData(prev => ({
      ...prev,
      target_tiers: prev.target_tiers?.length === allTiers.length ? [] : allTiers
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const uploadImage = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `content-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('content-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('content-media')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err) {
      console.error('Upload error:', err);
      throw new Error('Failed to upload image: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      category: '',
      location: '',
      event_date: '',
      event_time: '',
      rsvp_deadline: '',
      target_tiers: [],
      is_published: false
    });
    setImageFile(null);
    setImagePreview('');
    setEditingItem(null);
    setView('list');
    setError('');
    setSuccess('');
  };

  const handleCreate = () => {
    resetForm();
    setView('create');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    // Format rsvp_deadline for datetime-local input (remove timezone and seconds)
    let formattedRsvpDeadline = '';
    if (item.rsvp_deadline) {
      const date = new Date(item.rsvp_deadline);
      // Format as YYYY-MM-DDTHH:mm for datetime-local input
      formattedRsvpDeadline = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
    }
    
    setFormData({
      title: item.title,
      slug: item.slug,
      excerpt: item.excerpt,
      content: item.content,
      image_url: item.image_url,
      category: item.category,
      location: item.location || '',
      event_date: item.event_date ? item.event_date.split('T')[0] : '',
      event_time: item.event_time || '',
      rsvp_deadline: formattedRsvpDeadline,
      target_tiers: item.target_tiers || [],
      is_published: item.is_published
    });
    setImagePreview(item.image_url);
    setImageFile(null);
    setView('edit');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUploading(false);
    setError('');
    setSuccess('');

    try {
      let imageUrl = formData.image_url;

      // Upload new image if selected
      if (imageFile) {
        setUploading(true);
        imageUrl = await uploadImage(imageFile);
        setUploading(false);
      }

      // Validate image URL
      if (!imageUrl) {
        throw new Error('Please upload an image');
      }

      const table = activeTab === 'news' ? 'news' : 'events';
      const dataToSubmit = {
        title: formData.title,
        slug: formData.slug,
        excerpt: formData.excerpt,
        content: formData.content,
        image_url: imageUrl,
        category: formData.category,
        author_id: adminInfo.id,
        author_name: adminInfo.full_name,
        is_published: formData.is_published,
        published_at: formData.is_published ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      };

      // Add event-specific fields
      if (activeTab === 'events') {
        // Validate event fields
        if (!formData.event_date || !formData.location) {
          throw new Error('Event date and location are required');
        }
        if (!formData.event_time) {
          throw new Error('Event time is required');
        }
        if (!formData.rsvp_deadline) {
          throw new Error('RSVP deadline is required');
        }
        // Validate target tiers
        if (!formData.target_tiers || formData.target_tiers.length === 0) {
          throw new Error('Please select at least one membership tier for this event');
        }
        
        // Convert datetime-local to ISO string (maintains local timezone)
        const rsvpDeadlineDate = new Date(formData.rsvp_deadline);
        
        dataToSubmit.location = formData.location;
        dataToSubmit.event_date = formData.event_date;
        dataToSubmit.event_time = formData.event_time;
        dataToSubmit.rsvp_deadline = rsvpDeadlineDate.toISOString();
        dataToSubmit.target_tiers = formData.target_tiers;
      }

      if (editingItem) {
        // Update existing item
        const { error } = await supabase
          .from(table)
          .update(dataToSubmit)
          .eq('id', editingItem.id);

        if (error) throw error;
        setSuccess(`${activeTab === 'news' ? 'News' : 'Event'} updated successfully!`);
      } else {
        // Create new item
        const { error } = await supabase
          .from(table)
          .insert([dataToSubmit]);

        if (error) throw error;
        setSuccess(`${activeTab === 'news' ? 'News' : 'Event'} created successfully!`);
      }

      setTimeout(() => {
        resetForm();
        fetchData();
      }, 1500);
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${activeTab === 'news' ? 'news article' : 'event'}?`)) {
      return;
    }

    try {
      const table = activeTab === 'news' ? 'news' : 'events';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSuccess(`${activeTab === 'news' ? 'News' : 'Event'} deleted successfully!`);
      fetchData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete: ' + err.message);
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      const table = activeTab === 'news' ? 'news' : 'events';
      const newPublishState = !item.is_published;
      
      const { error } = await supabase
        .from(table)
        .update({
          is_published: newPublishState,
          published_at: newPublishState ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', item.id);

      if (error) throw error;
      
      setSuccess(`${activeTab === 'news' ? 'News' : 'Event'} ${newPublishState ? 'published' : 'unpublished'}!`);
      fetchData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Toggle publish error:', err);
      setError('Failed to update: ' + err.message);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('contentAdmin');
    navigate('/content-admin-login');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const currentItems = activeTab === 'news' ? newsItems : eventItems;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        color: '#ffffff',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
            <i className="fas fa-newspaper" style={{marginRight: '12px', color: '#CDB688'}}></i>
            Content Management
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
            Welcome, {adminInfo?.full_name}
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: '10px 20px',
            background: '#CDB688',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.opacity = '0.9'}
          onMouseOut={(e) => e.target.style.opacity = '1'}
        >
          <i className="fas fa-sign-out-alt" style={{marginRight: '8px'}}></i>
          Logout
        </button>
      </div>

      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Alerts */}
        {error && (
          <div style={{
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#c33',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: '#efe',
            border: '1px solid #cfc',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#3c3',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          background: '#ffffff',
          padding: '8px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <button
            onClick={() => {
              setActiveTab('news');
              setView('list');
              resetForm();
            }}
            style={{
              flex: 1,
              padding: '12px',
              background: activeTab === 'news' ? '#CDB688' : 'transparent',
              color: activeTab === 'news' ? '#ffffff' : '#666',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <i className="fas fa-newspaper" style={{marginRight: '8px'}}></i>
            News Articles ({newsItems.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('events');
              setView('list');
              resetForm();
            }}
            style={{
              flex: 1,
              padding: '12px',
              background: activeTab === 'events' ? '#CDB688' : 'transparent',
              color: activeTab === 'events' ? '#ffffff' : '#666',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <i className="fas fa-calendar-alt" style={{marginRight: '8px'}}></i>
            Events ({eventItems.length})
          </button>
        </div>

        {/* List View */}
        {view === 'list' && (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {activeTab === 'news' ? 'News Articles' : 'Events'}
              </h2>
              <button
                onClick={handleCreate}
                style={{
                  padding: '12px 24px',
                  background: '#CDB688',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(205, 182, 136, 0.3)'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <i className="fas fa-plus" style={{marginRight: '8px'}}></i>
                Create New {activeTab === 'news' ? 'Article' : 'Event'}
              </button>
            </div>

            {currentItems.length === 0 ? (
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                padding: '60px 20px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <i className={`fas fa-${activeTab === 'news' ? 'newspaper' : 'calendar'}`} 
                  style={{ fontSize: '48px', color: '#ddd', marginBottom: '16px' }}></i>
                <p style={{ color: '#666', fontSize: '16px' }}>
                  No {activeTab} yet. Create your first one!
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                {currentItems.map(item => (
                  <div key={item.id} style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease'
                  }}>
                    <img 
                      src={item.image_url} 
                      alt={item.title}
                      style={{
                        width: '100%',
                        height: '180px',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{ padding: '16px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          background: '#CDB688',
                          color: '#ffffff',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {item.category}
                        </span>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: item.is_published ? '#e8f5e9' : '#fff3e0',
                          color: item.is_published ? '#2e7d32' : '#e65100'
                        }}>
                          {item.is_published ? '✓ Published' : '○ Draft'}
                        </span>
                      </div>
                      
                      <h3 style={{
                        margin: '12px 0 8px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        lineHeight: '1.4',
                        color: '#1a1a1a'
                      }}>
                        {item.title}
                      </h3>
                      
                      <p style={{
                        margin: '0 0 12px 0',
                        fontSize: '13px',
                        color: '#666',
                        lineHeight: '1.5',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {item.excerpt}
                      </p>

                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        marginBottom: '12px',
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        {activeTab === 'events' && (
                          <>
                            <span>
                              <i className="far fa-calendar" style={{marginRight: '4px'}}></i>
                              {new Date(item.event_date).toLocaleDateString()}
                            </span>
                            {item.event_time && (() => {
                              // Format event_time from 24h to 12h
                              const [hours, minutes] = item.event_time.split(':');
                              const hour = parseInt(hours, 10);
                              const ampm = hour >= 12 ? 'PM' : 'AM';
                              const displayHour = hour % 12 || 12;
                              return (
                                <span>
                                  <i className="far fa-clock" style={{marginRight: '4px'}}></i>
                                  {`${displayHour}:${minutes} ${ampm}`}
                                </span>
                              );
                            })()}
                            <span>
                              <i className="fas fa-map-marker-alt" style={{marginRight: '4px'}}></i>
                              {item.location}
                            </span>
                            {item.rsvp_deadline && (
                              <span style={{ 
                                color: new Date(item.rsvp_deadline) < new Date() ? '#f44336' : '#999',
                                fontWeight: new Date(item.rsvp_deadline) < new Date() ? '600' : '400'
                              }}>
                                <i className="fas fa-stopwatch" style={{marginRight: '4px'}}></i>
                                RSVP by: {new Date(item.rsvp_deadline).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                                {new Date(item.rsvp_deadline) < new Date() && ' (Closed)'}
                              </span>
                            )}
                          </>
                        )}
                        {activeTab === 'news' && (
                          <span>
                            <i className="far fa-clock" style={{marginRight: '4px'}}></i>
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {/* Show target tiers for events */}
                      {activeTab === 'events' && item.target_tiers && item.target_tiers.length > 0 && (
                        <div style={{
                          marginBottom: '12px',
                          display: 'flex',
                          gap: '6px',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          <small style={{ 
                            color: '#666', 
                            fontSize: '11px',
                            fontWeight: '600',
                            marginRight: '4px'
                          }}>
                            VISIBLE TO:
                          </small>
                          {item.target_tiers.map(tier => (
                            <span
                              key={tier}
                              style={{
                                padding: '3px 8px',
                                background: tier === 'Platinum' ? '#e8eaf6' : 
                                           tier === 'Gold' ? '#fff8e1' : 
                                           tier === 'Silver' ? '#f5f5f5' : 
                                           tier === 'LGC' ? '#fef9e7' : '#e0e0e0',
                                color: tier === 'Platinum' ? '#3f51b5' : 
                                       tier === 'Gold' ? '#f57f17' : 
                                       tier === 'Silver' ? '#616161' : 
                                       tier === 'LGC' ? '#9a7d0a' : '#333',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '700',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                border: `1px solid ${
                                  tier === 'Platinum' ? '#c5cae9' : 
                                  tier === 'Gold' ? '#ffecb3' : 
                                  tier === 'Silver' ? '#e0e0e0' : 
                                  tier === 'LGC' ? '#f9e79f' : '#bdbdbd'
                                }`
                              }}
                            >
                              {tier === 'LGC' ? 'LONDON GOLD CLUB' : tier.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Show RSVP count for events */}
                      {activeTab === 'events' && (
                        <div style={{
                          marginBottom: '12px',
                          padding: '8px 12px',
                          background: '#e8f5e9',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-users" style={{ color: '#4caf50', fontSize: '14px' }}></i>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#2e7d32' }}>
                              {rsvpCounts[item.id] || 0} RSVP{(rsvpCounts[item.id] || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {(rsvpCounts[item.id] || 0) > 0 && (
                            <button
                              onClick={() => fetchEventRsvps(item.id)}
                              style={{
                                padding: '4px 12px',
                                background: '#4caf50',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = '#45a049'}
                              onMouseOut={(e) => e.currentTarget.style.background = '#4caf50'}
                            >
                              View Details
                            </button>
                          )}
                        </div>
                      )}

                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        paddingTop: '12px',
                        borderTop: '1px solid #f0f0f0'
                      }}>
                        <button
                          onClick={() => handleEdit(item)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: '#2196f3',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleTogglePublish(item)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: item.is_published ? '#ff9800' : '#4caf50',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <i className={`fas fa-${item.is_published ? 'eye-slash' : 'eye'}`}></i>
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: '#f44336',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Create/Edit Form */}
        {(view === 'create' || view === 'edit') && (
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {view === 'create' ? 'Create' : 'Edit'} {activeTab === 'news' ? 'News Article' : 'Event'}
              </h2>
              <button
                onClick={resetForm}
                style={{
                  padding: '8px 16px',
                  background: '#f5f5f5',
                  color: '#666',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-times" style={{marginRight: '8px'}}></i>
                Cancel
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Title <span style={{color: '#f44336'}}>*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter title..."
                    style={inputStyle}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Slug <span style={{color: '#f44336'}}>*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    placeholder="url-friendly-slug"
                    style={inputStyle}
                  />
                  <small style={{ color: '#999', fontSize: '12px' }}>
                    Auto-generated from title, but you can edit it
                  </small>
                </div>

                <div>
                  <label style={labelStyle}>
                    Category <span style={{color: '#f44336'}}>*</span>
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    placeholder={activeTab === 'news' ? 'Company News, Market Analysis...' : 'Gala, Seminar, Networking...'}
                    style={inputStyle}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Image <span style={{color: '#f44336'}}>*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImageChange}
                    style={{
                      ...inputStyle,
                      padding: '8px',
                      cursor: 'pointer'
                    }}
                  />
                  <small style={{ color: '#999', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Accepted formats: JPG, PNG, GIF, WebP (Max 5MB)
                  </small>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div style={{
                      marginTop: '12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '8px',
                      background: '#f9f9f9'
                    }}>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }}
                      />
                      <p style={{
                        fontSize: '12px',
                        color: '#666',
                        marginTop: '8px',
                        marginBottom: 0,
                        textAlign: 'center'
                      }}>
                        {imageFile ? 'New image selected' : 'Current image'}
                      </p>
                    </div>
                  )}
                </div>

                {activeTab === 'events' && (
                  <>
                    <div>
                      <label style={labelStyle}>
                        Event Date <span style={{color: '#f44336'}}>*</span>
                      </label>
                      <input
                        type="date"
                        name="event_date"
                        value={formData.event_date}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>
                        Event Time <span style={{color: '#f44336'}}>*</span>
                      </label>
                      <input
                        type="time"
                        name="event_time"
                        value={formData.event_time}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>
                        Location <span style={{color: '#f44336'}}>*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                        placeholder="Dubai, London..."
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>
                        RSVP Deadline <span style={{color: '#f44336'}}>*</span>
                      </label>
                      <small style={{ color: '#999', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        Users cannot RSVP after this date/time
                      </small>
                      <input
                        type="datetime-local"
                        name="rsvp_deadline"
                        value={formData.rsvp_deadline}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={labelStyle}>
                        Target Membership Tiers <span style={{color: '#f44336'}}>*</span>
                      </label>
                      <small style={{ color: '#999', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        Select which membership tiers can see this event
                      </small>
                      
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        padding: '16px',
                        background: '#f9f9f9',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0'
                      }}>
                        {/* Select All Button */}
                        <button
                          type="button"
                          onClick={handleSelectAllTiers}
                          style={{
                            padding: '8px 16px',
                            background: (formData.target_tiers?.length === 4) ? '#CDB688' : '#ffffff',
                            color: (formData.target_tiers?.length === 4) ? '#ffffff' : '#333',
                            border: '2px solid #CDB688',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            width: '100%'
                          }}
                        >
                          <i className={`fas fa-${(formData.target_tiers?.length === 4) ? 'check-square' : 'square'}`} 
                            style={{marginRight: '8px'}}></i>
                          {(formData.target_tiers?.length === 4) ? 'Deselect All' : 'Select All Tiers'}
                        </button>

                        {/* Individual Tier Checkboxes */}
                        {['Silver', 'Gold', 'Platinum', 'LGC'].map(tier => (
                          <label
                            key={tier}
                            style={{
                              flex: '1 1 calc(50% - 12px)',
                              minWidth: '150px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '12px 16px',
                              background: (formData.target_tiers || []).includes(tier) ? '#e8f5e9' : '#ffffff',
                              border: `2px solid ${(formData.target_tiers || []).includes(tier) ? '#4caf50' : '#e0e0e0'}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}
                            onMouseOver={(e) => {
                              if (!(formData.target_tiers || []).includes(tier)) {
                                e.currentTarget.style.borderColor = '#CDB688';
                                e.currentTarget.style.background = '#fef9f0';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!(formData.target_tiers || []).includes(tier)) {
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.background = '#ffffff';
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={(formData.target_tiers || []).includes(tier)}
                              onChange={() => handleTierChange(tier)}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: '#4caf50'
                              }}
                            />
                            <span style={{ 
                              color: (formData.target_tiers || []).includes(tier) ? '#2e7d32' : '#333'
                            }}>
                              {tier === 'LGC' ? 'London Gold Club' : tier}
                            </span>
                            {(formData.target_tiers || []).includes(tier) && (
                              <i className="fas fa-check-circle" 
                                style={{ marginLeft: 'auto', color: '#4caf50' }}></i>
                            )}
                          </label>
                        ))}
                      </div>

                      {formData.target_tiers?.length > 0 && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          background: '#e3f2fd',
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: '#1976d2'
                        }}>
                          <i className="fas fa-info-circle" style={{marginRight: '6px'}}></i>
                          Selected: {formData.target_tiers.join(', ')}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Excerpt <span style={{color: '#f44336'}}>*</span>
                  </label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="Brief summary (shown in cards)..."
                    style={{...inputStyle, resize: 'vertical'}}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Full Content <span style={{color: '#f44336'}}>*</span>
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    required
                    rows="10"
                    placeholder="Full article/event content..."
                    style={{...inputStyle, resize: 'vertical'}}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={formData.is_published}
                      onChange={handleInputChange}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    Publish immediately
                  </label>
                  <small style={{ color: '#999', fontSize: '12px', marginLeft: '26px', display: 'block' }}>
                    Uncheck to save as draft
                  </small>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '20px',
                borderTop: '1px solid #f0f0f0'
              }}>
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: (submitting || uploading) ? '#ccc' : '#CDB688',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: (submitting || uploading) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {uploading ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{marginRight: '8px'}}></i>
                      Uploading image...
                    </>
                  ) : submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{marginRight: '8px'}}></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" style={{marginRight: '8px'}}></i>
                      {view === 'create' ? 'Create' : 'Update'} {activeTab === 'news' ? 'Article' : 'Event'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '14px 24px',
                    background: '#f5f5f5',
                    color: '#666',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* RSVP Modal */}
      {showRsvpModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowRsvpModal(false)}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '2px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>
                <i className="fas fa-users" style={{ marginRight: '10px', color: '#4caf50' }}></i>
                Event RSVPs ({selectedEventRsvps.length})
              </h3>
              <button
                onClick={() => setShowRsvpModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.color = '#333';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#999';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '20px 24px',
              overflowY: 'auto',
              flex: 1
            }}>
              {selectedEventRsvps.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#999'
                }}>
                  <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }}></i>
                  <p>No RSVPs yet for this event.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedEventRsvps.map((rsvp, index) => (
                    <div
                      key={rsvp.id}
                      style={{
                        padding: '16px',
                        background: '#f9f9f9',
                        borderRadius: '12px',
                        border: '2px solid #e0e0e0',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#CDB688';
                        e.currentTarget.style.background = '#fefdfb';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.background = '#f9f9f9';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <i className="fas fa-user-circle" style={{ color: '#CDB688', fontSize: '18px' }}></i>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>
                              {rsvp.user_name}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <i className="fas fa-envelope" style={{ color: '#999', fontSize: '12px', width: '14px' }}></i>
                            <span style={{ fontSize: '13px', color: '#666' }}>
                              {rsvp.user_email}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fas fa-calendar" style={{ color: '#999', fontSize: '12px', width: '14px' }}></i>
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              RSVP'd: {new Date(rsvp.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                        <span style={{
                          padding: '4px 12px',
                          background: rsvp.membership_tier === 'Platinum' ? '#e8eaf6' : 
                                     rsvp.membership_tier === 'Gold' ? '#fff8e1' : 
                                     rsvp.membership_tier === 'Silver' ? '#f5f5f5' : 
                                     rsvp.membership_tier === 'LGC' ? '#fef9e7' : '#e0e0e0',
                          color: rsvp.membership_tier === 'Platinum' ? '#3f51b5' : 
                                 rsvp.membership_tier === 'Gold' ? '#f57f17' : 
                                 rsvp.membership_tier === 'Silver' ? '#616161' : 
                                 rsvp.membership_tier === 'LGC' ? '#9a7d0a' : '#333',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '700',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap'
                        }}>
                          {rsvp.membership_tier === 'LGC' ? 'LGC' : rsvp.membership_tier}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '2px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#fafafa'
            }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                Total confirmed RSVPs: <strong>{selectedEventRsvps.length}</strong>
              </div>
              <button
                onClick={() => setShowRsvpModal(false)}
                style={{
                  padding: '10px 24px',
                  background: '#CDB688',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#b39556'}
                onMouseOut={(e) => e.currentTarget.style.background = '#CDB688'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: '600',
  color: '#333'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  fontSize: '14px',
  transition: 'border-color 0.3s ease',
  outline: 'none'
};

export default ContentAdminDashboard;
